import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import { Flag, Plus } from "lucide-react";
import { toast } from "sonner";

const sevColor = (s) =>
  s === "critical" ? "rose" : s === "high" ? "amber" : s === "medium" ? "cyan" : "slate";

const Incidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ title: "", severity: "medium", mitre: "" });

  const load = useCallback(
    () => api.get("/incidents").then(({ data }) => setIncidents(data)),
    []
  );
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.title.trim()) return;
    try {
      await api.post("/incidents", draft);
      toast.success("Incident logged");
      setOpen(false);
      setDraft({ title: "", severity: "medium", mitre: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Defence / Response"
        title="Incident Response"
        subtitle="Live incident queue with MITRE mapping, ownership, and SOAR-ready playbook hooks."
        actions={
          <TButton data-testid="new-incident" icon={Plus} onClick={() => setOpen(!open)}>
            New Incident
          </TButton>
        }
      />

      {open && (
        <div className="csx-card p-4 mb-6 space-y-3">
          <input
            data-testid="incident-title"
            className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-cyan-500/70"
            placeholder="Incident title..."
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <select className="bg-slate-950 border border-slate-800 px-3 py-2 text-sm" value={draft.severity}
              onChange={(e) => setDraft({ ...draft, severity: e.target.value })}>
              {["critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input
              className="bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono"
              placeholder="MITRE (e.g., T1059.001)"
              value={draft.mitre}
              onChange={(e) => setDraft({ ...draft, mitre: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-2">
            <TButton data-testid="incident-save" onClick={create}>Save</TButton>
          </div>
        </div>
      )}

      <div className="csx-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 bg-slate-950/60">
              <th className="py-2.5 px-4">Incident</th>
              <th className="px-4">Severity</th>
              <th className="px-4">Status</th>
              <th className="px-4">MITRE</th>
              <th className="px-4">Owner</th>
              <th className="px-4">When</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((i) => {
              const c = sevColor(i.severity);
              return (
                <tr key={i.id} data-testid={`incident-${i.id}`} className="border-b border-slate-900 hover:bg-slate-900/40">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Flag className={`h-3.5 w-3.5 text-${c}-400`} />
                      <span className="text-slate-100">{i.title}</span>
                    </div>
                  </td>
                  <td className="px-4">
                    <span className={`text-[10px] uppercase tracking-widest border border-${c}-500/60 text-${c}-300 bg-${c}-500/10 px-1.5 py-0.5`}>
                      {i.severity}
                    </span>
                  </td>
                  <td className="px-4 text-slate-400 uppercase text-[10px] tracking-widest">{i.status}</td>
                  <td className="px-4 font-mono text-cyan-300 text-xs">{i.mitre || "—"}</td>
                  <td className="px-4 text-slate-400 text-xs">{i.owner || "—"}</td>
                  <td className="px-4 text-slate-500 text-xs">{new Date(i.created_at).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Incidents;
